import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, Clock, ShieldCheck, Download, Sparkles } from 'lucide-react';
import { Button } from './components/Button';
import { Footer } from './components/Footer';
const serif = {
  fontFamily: "'Fraunces', serif"
};
const sans = {
  fontFamily: "'Inter', sans-serif"
};
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} animate={{
  opacity: 1,
  y: 0
}} transition={{
  duration: 0.7,
  delay,
  ease: 'easeOut'
}} data-id="element-0">
    {children}
  </motion.div>;
export function App() {
  const scrollToOffer = () => {
    document.getElementById('offer')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen w-full bg-[#FAF6F0] text-[#2B2A26]" style={sans} data-id="element-1">
      {/* HERO SECTION */}
      <section className="relative pt-20 pb-24 md:pt-32 md:pb-32 px-6 overflow-hidden" data-id="element-2">
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-3">
          <FadeIn data-id="element-4">
            <span className="inline-block py-1 px-3 rounded-full bg-[#D99A5A]/20 text-[#C77D5A] font-semibold text-sm tracking-wide uppercase mb-6" data-id="element-5">
              Protocolo S.O.S. de 21 Días
            </span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl leading-[1.1] mb-8 text-[#2B2A26]" style={serif} data-id="element-6">
              El Fin del{' '}
              <span className="italic text-[#C77D5A]" data-id="element-7">"Botón que Aprieta"</span>
              :<br data-id="element-8" />
              El Protocolo Anti-Inflamación para la Mujer en USA
            </h1>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-9">
            <p className="text-lg md:text-2xl text-[#2B2A26]/80 max-w-3xl mx-auto leading-relaxed mb-10 font-light" data-id="element-10">
              Desinflama tu abdomen, regula tu cortisol y recupera la libertad
              de usar tu ropa favorita en 21 días{' '}
              <span className="font-medium" data-id="element-11">
                (sin dietas extremas ni horas de gimnasio)
              </span>
              .
            </p>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-12">
            <Button onClick={e => {
            e.preventDefault();
            scrollToOffer();
          }} className="mb-6" data-id="element-13">
              ¡QUIERO DESINFLAMARME Y CERRAR MI JEAN SIN DOLOR!
            </Button>
            <p className="text-sm text-[#2B2A26]/60 flex items-center justify-center gap-2" data-id="element-14">
              <ShieldCheck className="w-4 h-4" data-id="element-15" /> Acceso instantáneo. Compras
              100% seguras.
            </p>
          </FadeIn>
        </div>

        {/* Decorative background elements */}
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-[#D99A5A]/10 blur-3xl -z-10" data-id="element-16" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-[#C77D5A]/10 blur-3xl -z-10" data-id="element-17" />
      </section>

      {/* OPENING HOOK */}
      <section className="py-20 bg-white px-6" data-id="element-18">
        <div className="max-w-3xl mx-auto" data-id="element-19">
          <FadeIn data-id="element-20">
            <p className="text-2xl md:text-3xl leading-relaxed text-[#2B2A26] mb-8" style={serif} data-id="element-21">
              Sabes exactamente de lo que hablo. Estás sentada en el trabajo, en
              el bus o cenando, y de repente sientes ese dolor sordo y familiar:{' '}
              <span className="italic text-[#C77D5A]" data-id="element-22">
                el botón de tu jean clavándose en tu abdomen.
              </span>
            </p>
            <p className="text-lg text-[#2B2A26]/80 leading-relaxed mb-8" data-id="element-23">
              Tienes que desabrocharlo discretamente o cubrirte con una chaqueta
              porque te sientes como un globo a punto de explotar.
            </p>
            <div className="p-8 bg-[#FAF6F0] rounded-2xl border border-[#D99A5A]/30 relative" data-id="element-24">
              <Sparkles className="absolute top-4 left-4 text-[#D99A5A]/40 w-8 h-8" data-id="element-25" />
              <p className="text-xl font-medium text-[#2B2A26] relative z-10 text-center" data-id="element-26">
                No es que "te descuidaste" al llegar a este país. Es que tu
                cuerpo está reaccionando al estrés de vivir en USA de la única
                forma que sabe:{' '}
                <span className="text-[#C77D5A] italic" data-id="element-27">
                  inflamándose para protegerte.
                </span>
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="py-24 px-6 relative" data-id="element-28">
        <div className="max-w-3xl mx-auto" data-id="element-29">
          <FadeIn data-id="element-30">
            <h2 className="text-3xl md:text-5xl mb-8 text-center" style={serif} data-id="element-31">
              Desde que emigraste, tu cuerpo entró en{' '}
              <span className="italic text-[#C77D5A]" data-id="element-32">
                "Modo Supervivencia"
              </span>
              .
            </h2>
            <div className="space-y-6 text-lg text-[#2B2A26]/80 leading-relaxed" data-id="element-33">
              <p data-id="element-34">
                El idioma, la soledad, las facturas y el ritmo frenético de este
                país dispararon tu cortisol. Esta hormona le ordena a tus
                células retener cada gota de agua y cada gramo de grasa en tu
                vientre como reserva de emergencia.
              </p>
              <p data-id="element-35">
                Por eso, aunque dejes de comer, el espejo te devuelve la imagen
                de una mujer cansada, con la cara hinchada y un abdomen que ya
                no reconoce.
              </p>
              <p className="text-2xl text-[#2B2A26] border-l-4 border-[#C77D5A] pl-6 py-2 my-8" style={serif} data-id="element-36">
                No es grasa común; es{' '}
                <span className="italic" data-id="element-37">inflamación migratoria.</span>
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="py-24 bg-[#2B2A26] text-[#FAF6F0] px-6" data-id="element-38">
        <div className="max-w-4xl mx-auto" data-id="element-39">
          <FadeIn data-id="element-40">
            <h2 className="text-3xl md:text-5xl text-center mb-16" style={serif} data-id="element-41">
              Por qué lo que has intentado{' '}
              <span className="italic text-[#D99A5A]" data-id="element-42">no funciona</span>
            </h2>

            <div className="grid gap-6" data-id="element-43">
              {['Las dietas de "pocas calorías" estresan más a tu organismo, haciendo que retenga más líquidos.', 'El café americano que usas para sobrevivir al cansancio agota tus glándulas suprarrenales y empeora el "bloating".', 'Matarte en el gimnasio después de trabajar 10 horas solo eleva más el cortisol, bloqueando cualquier quema de grasa.', 'Ignorar el impacto de las luces artificiales y las pantallas en tus ojos está destruyendo tu descanso biológico.'].map((item, i) => <div key={i} className="flex gap-4 items-start bg-white/5 p-6 rounded-xl border border-white/10" data-id="element-44">
                  <XCircle className="w-6 h-6 text-[#C77D5A] shrink-0 mt-1" data-id="element-45" />
                  <p className="text-lg text-[#FAF6F0]/90" data-id="element-46">{item}</p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* THE UNIQUE MECHANISM */}
      <section className="py-24 px-6 bg-white" data-id="element-47">
        <div className="max-w-4xl mx-auto text-center" data-id="element-48">
          <FadeIn data-id="element-49">
            <span className="text-[#C77D5A] font-semibold tracking-wide uppercase mb-4 block" data-id="element-50">
              La Solución Biológica
            </span>
            <h2 className="text-4xl md:text-5xl mb-8" style={serif} data-id="element-51">
              El Protocolo S.O.S.
              <br data-id="element-52" />
              <span className="text-2xl md:text-3xl text-[#2B2A26]/60 italic" data-id="element-53">
                (Señales de Seguridad Biológica)
              </span>
            </h2>

            <p className="text-xl text-[#2B2A26]/80 leading-relaxed mb-10" data-id="element-54">
              Para que tu abdomen se desinfle, primero tienes que convencer a tu
              cerebro de que "estás a salvo". Mi protocolo utiliza bio-hacks
              específicos —como la hidratación mineral estratégica y el bloqueo
              de luz azul— para apagar la alarma de incendio de tu sistema
              nervioso.
            </p>

            <div className="inline-block bg-[#FAF6F0] px-8 py-6 rounded-2xl border border-[#D99A5A]/30" data-id="element-55">
              <p className="text-2xl text-[#C77D5A]" style={serif} data-id="element-56">
                Cuando el cortisol baja, la inflamación se va.
                <br data-id="element-57" />
                <span className="italic text-[#2B2A26]" data-id="element-58">
                  Es biología, no fuerza de voluntad.
                </span>
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-24 px-6 bg-[#FAF6F0]" data-id="element-59">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center" data-id="element-60">
          <div data-id="element-61">
            <FadeIn data-id="element-62">
              <h2 className="text-3xl md:text-5xl mb-10" style={serif} data-id="element-63">
                Todo lo que recibes hoy:
              </h2>
              <div className="space-y-6" data-id="element-64">
                {[{
                title: 'Ebook Cortisol & El Sueño Americano',
                desc: 'El manual paso a paso para entender y sanar tu cuerpo.'
              }, {
                title: 'Protocolo de Reset de 21 Días',
                desc: 'Pasos diarios exactos para desinflamar el nervio vago.'
              }, {
                title: 'La Técnica del "Escudo Nocturno"',
                desc: 'Cómo usar la luz y las gafas blue-blocker para dormir y adelgazar.'
              }, {
                title: 'Guía de Supermercado Anti-Inflamatoria',
                desc: 'Qué marcas comprar en USA para limpiar tu microbiota.'
              }, {
                title: 'Checklist de Emergencia para el "Momento del Botón"',
                desc: 'Qué hacer cuando la hinchazón ataca de golpe.'
              }].map((item, i) => <div key={i} className="flex gap-4 items-start" data-id="element-65">
                    <CheckCircle2 className="w-6 h-6 text-[#2C4A3B] shrink-0 mt-1" data-id="element-66" />
                    <div data-id="element-67">
                      <h3 className="font-semibold text-lg text-[#2B2A26]" data-id="element-68">
                        {item.title}
                      </h3>
                      <p className="text-[#2B2A26]/70" data-id="element-69">{item.desc}</p>
                    </div>
                  </div>)}
              </div>
            </FadeIn>
          </div>
          <FadeIn delay={0.2} data-id="element-70">
            <div className="relative" data-id="element-71">
              <div className="absolute inset-0 bg-[#D99A5A]/20 rounded-3xl transform rotate-3 scale-105 -z-10" data-id="element-72"></div>
              <img src="https://cdn.magicpatterns.com/uploads/iTg5rwTjXUaK6VzXcKNJs5/CORTISOL_Y_SUENO_AMERICANO.png" alt="Cortisol y el Sueño Americano Ebook" className="rounded-3xl shadow-xl w-full object-cover" data-id="element-73" />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHO THIS IS FOR / NOT FOR */}
      <section className="py-24 px-6 bg-white" data-id="element-74">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12" data-id="element-75">
          <FadeIn data-id="element-76">
            <div className="bg-[#FAF6F0] p-8 rounded-3xl h-full border border-[#2C4A3B]/10" data-id="element-77">
              <h3 className="text-2xl mb-8 text-[#2C4A3B] flex items-center gap-3" style={serif} data-id="element-78">
                <CheckCircle2 className="w-8 h-8" data-id="element-79" /> Esto ES para ti si:
              </h3>
              <ul className="space-y-4" data-id="element-80">
                {['Mujeres que emigraron a USA y se sienten "hinchadas" permanentemente.', 'Quienes sufren la presión dolorosa del jean al sentarse cada día.', 'Mujeres que trabajan mucho, duermen poco y sienten que han perdido su belleza de origen.', 'Quienes quieren soluciones prácticas que se adaptan a la vida real en este país.'].map((item, i) => <li key={i} className="flex gap-3 text-[#2B2A26]/80" data-id="element-81">
                    <span className="text-[#2C4A3B] mt-1" data-id="element-82">•</span> {item}
                  </li>)}
              </ul>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-83">
            <div className="bg-[#2B2A26]/5 p-8 rounded-3xl h-full border border-[#2B2A26]/10" data-id="element-84">
              <h3 className="text-2xl mb-8 text-[#2B2A26]/60 flex items-center gap-3" style={serif} data-id="element-85">
                <XCircle className="w-8 h-8" data-id="element-86" /> Esto NO es para ti si:
              </h3>
              <ul className="space-y-4" data-id="element-87">
                {['Personas que buscan pastillas para adelgazar o soluciones mágicas sin cambiar hábitos.', 'Quienes no están dispuestas a soltar el teléfono una hora antes de dormir.', 'Quienes prefieren seguir contando calorías en lugar de sanar sus hormonas.'].map((item, i) => <li key={i} className="flex gap-3 text-[#2B2A26]/60" data-id="element-88">
                    <span className="text-[#2B2A26]/40 mt-1" data-id="element-89">•</span> {item}
                  </li>)}
              </ul>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 px-6 bg-[#2C4A3B] text-[#FAF6F0]" data-id="element-90">
        <div className="max-w-4xl mx-auto" data-id="element-91">
          <FadeIn data-id="element-92">
            <h2 className="text-3xl md:text-5xl text-center mb-16" style={serif} data-id="element-93">
              Cómo funciona
            </h2>
            <div className="grid md:grid-cols-3 gap-8" data-id="element-94">
              {[{
              icon: Download,
              title: 'Descarga Directa',
              desc: 'Recibes el material en tu correo inmediatamente.'
            }, {
              icon: Clock,
              title: 'Implementa el S.O.S.',
              desc: 'Empiezas a enviar señales de seguridad a tu sistema nervioso desde el día 1.'
            }, {
              icon: Sparkles,
              title: 'Siente el Alivio',
              desc: 'En una semana, notarás tu cara más definida y tu jean cerrando sin esfuerzo.'
            }].map((step, i) => <div key={i} className="text-center" data-id="element-95">
                  <div className="w-16 h-16 mx-auto bg-[#C77D5A] rounded-full flex items-center justify-center mb-6" data-id="element-96">
                    <step.icon className="w-8 h-8 text-[#FAF6F0]" data-id="element-97" />
                  </div>
                  <h3 className="text-xl mb-3" style={serif} data-id="element-98">
                    {step.title}
                  </h3>
                  <p className="text-[#FAF6F0]/80" data-id="element-99">{step.desc}</p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* PROOF */}
      <section className="py-24 px-6 bg-[#FAF6F0]" data-id="element-100">
        <div className="max-w-3xl mx-auto text-center" data-id="element-101">
          <FadeIn data-id="element-102">
            <h2 className="text-3xl md:text-5xl mb-8" style={serif} data-id="element-103">
              Lo que sucede en 21 días
            </h2>
            <p className="text-xl text-[#2B2A26]/80 leading-relaxed mb-8" data-id="element-104">
              No solo verás cambios en la balanza. Lo primero que notarás es que
              dejas de despertarte con los ojos inflamados. Tu energía será
              estable durante el día y, lo más importante,{' '}
              <span className="font-medium text-[#C77D5A]" data-id="element-105">
                llegarás a la noche sin sentir que tu abdomen va a explotar.
              </span>
            </p>
            <p className="text-2xl italic text-[#2B2A26]" style={serif} data-id="element-106">
              Tu cuerpo finalmente dejará de pelear contra el entorno.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-white" data-id="element-107">
        <div className="max-w-3xl mx-auto" data-id="element-108">
          <FadeIn data-id="element-109">
            <h2 className="text-3xl md:text-5xl text-center mb-16" style={serif} data-id="element-110">
              Preguntas Frecuentes
            </h2>
            <div className="space-y-6" data-id="element-111">
              {[{
              q: '¿Es difícil de seguir si trabajo mucho?',
              a: 'No, los pasos toman menos de 10 minutos al día.'
            }, {
              q: '¿Tengo que comprar comida orgánica cara?',
              a: 'No, te enseñamos a elegir inteligentemente en supermercados comunes.'
            }, {
              q: '¿Realmente funcionan las gafas de luz azul?',
              a: 'Son la herramienta más subestimada para bajar el cortisol nocturno y verás el efecto en tu rostro de inmediato.'
            }, {
              q: '¿Esto es una dieta?',
              a: 'Es un protocolo de regulación hormonal. Puedes seguir comiendo lo que te gusta, ajustando la calidad y el tiempo.'
            }].map((faq, i) => <div key={i} className="bg-[#FAF6F0] p-6 rounded-2xl" data-id="element-112">
                  <h4 className="font-semibold text-lg text-[#2B2A26] mb-2" data-id="element-113">
                    {faq.q}
                  </h4>
                  <p className="text-[#2B2A26]/80" data-id="element-114">{faq.a}</p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* OFFER + CTA */}
      <section id="offer" className="py-24 px-6 bg-[#2B2A26] text-[#FAF6F0] relative overflow-hidden" data-id="element-115">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-full bg-[#C77D5A]/10 blur-3xl rounded-full -z-10" data-id="element-116"></div>
        <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-117">
          <FadeIn data-id="element-118">
            <h2 className="text-4xl md:text-6xl mb-6" style={serif} data-id="element-119">
              Recupera la comodidad de tu propio cuerpo
            </h2>
            <p className="text-xl text-[#FAF6F0]/80 mb-12" data-id="element-120">
              por el precio de una cena rápida.
            </p>

            <div className="bg-white/10 backdrop-blur-md border border-white/20 p-8 md:p-12 rounded-3xl mb-10" data-id="element-121">
              <p className="text-sm uppercase tracking-widest text-[#D99A5A] mb-4 font-semibold" data-id="element-122">
                Oferta por Tiempo Limitado
              </p>
              <div className="flex items-end justify-center gap-4 mb-2 flex-wrap" data-id="element-123">
                <p className="text-3xl md:text-4xl text-white/40 line-through decoration-[#C77D5A] decoration-2" style={serif} data-id="element-124">
                  $27
                </p>
                <p className="text-6xl md:text-8xl text-white leading-none" style={serif} data-id="element-125">
                  $6.39 <span className="text-2xl text-white/60" data-id="element-126">USD</span>
                </p>
              </div>
              <p className="text-sm text-[#D99A5A] mb-8 font-semibold tracking-wide" data-id="element-127">
                Ahorras más del 75% hoy
              </p>

              <Button className="w-full text-xl py-6" data-id="element-128">
                ¡QUIERO DESINFLAMARME Y CERRAR MI JEAN SIN DOLOR!
              </Button>

              <div className="mt-6 flex items-center justify-center gap-2 text-[#FAF6F0]/60 text-sm" data-id="element-129">
                <ShieldCheck className="w-5 h-5" data-id="element-130" />
                <span data-id="element-131">Acceso instantáneo. Compras 100% seguras.</span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      <Footer data-id="element-132" />
    </div>;
}